import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { saveCurrentState, loadCurrentState } from '../storage';
import { ChevronUp, ChevronLeft } from 'lucide-react';
import ConfirmationDialog from '../../../components/dialogs/ConfirmationDialog';
import './Apgar.scss';


interface ApgarScores {
  pulse: { min1?: number; min5?: number; min10?: number };
  breathing: { min1?: number; min5?: number; min10?: number };
  muscleTone: { min1?: number; min5?: number; min10?: number };
  skinColor: { min1?: number; min5?: number; min10?: number };
  touchResponse: { min1?: number; min5?: number; min10?: number };
}

interface TimerState {
  startTime: number | null;
  isRunning: boolean;
  notifiedMinutes: number[];
}

interface NotificationState {
  minute: 1 | 5 | 10;
}

// Audio hook for timer beeps
const useTimerAudio = () => {
  const audioContextRef = useRef<AudioContext | null>(null);

  const initAudio = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
  }, []);

  const playBeep = useCallback(() => {
    try {
      if (!audioContextRef.current) return;

      const ctx = audioContextRef.current;

      // Resume if suspended (happens on mobile when tab is backgrounded)
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }

      // Only play if context is running (avoid errors on mobile)
      if (ctx.state !== 'running') return;

      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(800, ctx.currentTime);

      gainNode.gain.setValueAtTime(0, ctx.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.8, ctx.currentTime + 0.01);
      gainNode.gain.linearRampToValueAtTime(0.8, ctx.currentTime + 0.15);
      gainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.2);

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.2);
    } catch (error) {
      console.warn('Audio playback failed:', error);
    }
  }, []);

  const cleanup = useCallback(() => {
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  }, []);

  return { initAudio, playBeep, cleanup, audioContextRef };
};

// Format time as MM:SS
const formatTimeMMSS = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

// In-app notification component - uses Portal to render to document.body
const ApgarTimerNotification: React.FC<{
  minute: 1 | 5 | 10;
  onDismiss: () => void;
  onNavigate: () => void;
}> = ({ minute, onDismiss, onNavigate }) => {
  return createPortal(
    <div className="apgar-timer-notification" onClick={onDismiss}>
      <div className="notification-card" onClick={(e) => e.stopPropagation()}>
        <div className="notification-content">
          <span>הגיע הזמן לציון אפגר {minute} {minute === 1 ? 'דקה' : 'דקות'}!</span>
          <button onClick={onDismiss} className="dismiss-btn" aria-label="סגור">×</button>
        </div>
        <button onClick={onNavigate} className="navigate-btn">
          עבור למילוי
        </button>
      </div>
    </div>,
    document.body
  );
};

export const useApgarStatus = () => {
  const saved = loadCurrentState();
  const apgar = saved?.apgar || {};
  
  const calculateScore = (column: 'min1' | 'min5' | 'min10'): number | null => {
    const scores = [
      apgar.pulse?.[column],
      apgar.breathing?.[column],
      apgar.muscleTone?.[column],
      apgar.skinColor?.[column],
      apgar.touchResponse?.[column]
    ];
    
    if (scores.some(score => score === undefined)) {
      return null;
    }
    
    return scores.reduce((sum: number, score) => sum + (score ?? 0), 0);
  };
  
  const min1Score = calculateScore('min1');
  const min5Score = calculateScore('min5');
  const min10Score = calculateScore('min10');
  
  return { 
    min1: min1Score,
    min5: min5Score,
    min10: min10Score
  };
};

const Apgar: React.FC = () => {
  const [showGuide, setShowGuide] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    min1: true,
    min5: false,
    min10: false
  });
  const [apgarScores, setApgarScores] = useState<ApgarScores>(() => {
    const saved = loadCurrentState();
    const savedApgar = saved?.apgar;
    return {
      pulse: savedApgar?.pulse || {},
      breathing: savedApgar?.breathing || {},
      muscleTone: savedApgar?.muscleTone || {},
      skinColor: savedApgar?.skinColor || {},
      touchResponse: savedApgar?.touchResponse || {}
    };
  });

  // Timer state
  const [timerState, setTimerState] = useState<TimerState>(() => {
    const saved = loadCurrentState();
    return saved?.apgarTimer || {
      startTime: null,
      isRunning: false,
      notifiedMinutes: []
    };
  });
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [showStopDialog, setShowStopDialog] = useState(false);
  const [activeNotification, setActiveNotification] = useState<NotificationState | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const soundPlayedRef = useRef<Set<number>>(new Set());

  // Audio hook
  const { initAudio, playBeep, cleanup: cleanupAudio, audioContextRef } = useTimerAudio();

  useEffect(() => {
    saveCurrentState({ apgar: apgarScores }, 'apgar');
  }, [apgarScores]);

  // Timer interval management
  useEffect(() => {
    if (timerState.isRunning && timerState.startTime) {
      intervalRef.current = setInterval(() => {
        setCurrentTime(Date.now());
      }, 100); // Update every 100ms for smooth display
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [timerState.isRunning, timerState.startTime]);

  // Check for notifications
  useEffect(() => {
    if (!timerState.isRunning || !timerState.startTime) return;

    const elapsed = Math.floor((currentTime - timerState.startTime) / 1000);
    const notifiedMinutes = timerState.notifiedMinutes || [];

    // Check 1 minute
    if (elapsed >= 60 && !notifiedMinutes.includes(1)) {
      triggerNotification(1);
    }

    // Check 5 minutes
    if (elapsed >= 300 && !notifiedMinutes.includes(5)) {
      triggerNotification(5);
    }

    // Check 10 minutes - auto-stop
    if (elapsed >= 600 && !notifiedMinutes.includes(10)) {
      triggerNotification(10);
      stopTimer(true); // Auto-stop
    }
  }, [currentTime, timerState]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      cleanupAudio();
    };
  }, [cleanupAudio]);

  // Multi-tab synchronization
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key?.includes('current_new_preemie_state')) {
        const saved = loadCurrentState();
        if (saved?.apgarTimer) {
          setTimerState(saved.apgarTimer);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Handle page refresh - check if timer should have completed
  useEffect(() => {
    if (timerState.isRunning && timerState.startTime) {
      const elapsed = Math.floor((Date.now() - timerState.startTime) / 1000);

      // If timer passed 10 minutes while page was closed, auto-stop
      if (elapsed >= 600) {
        stopTimer(true);
      }
    }
  }, []); // Run once on mount

  // Handle visibility change (tab/app switching on mobile)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && timerState.isRunning) {
        // Immediately update current time when becoming visible
        setCurrentTime(Date.now());

        // Resume AudioContext if suspended (happens on mobile when backgrounded)
        if (audioContextRef.current?.state === 'suspended') {
          audioContextRef.current.resume().catch(() => {});
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [timerState.isRunning, audioContextRef]);

  // Request browser notification permission
  const requestNotificationPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      try {
        await Notification.requestPermission();
      } catch (error) {
        console.warn('Failed to request notification permission:', error);
      }
    }
  };

  // Show browser notification
  const showBrowserNotification = (minute: 1 | 5 | 10) => {
    try {
      if ('Notification' in window && Notification.permission === 'granted') {
        const n = new Notification('שעון אפגר', {
          body: `הגיע הזמן לציון אפגר ${minute} ${minute === 1 ? 'דקה' : 'דקות'}!`,
          icon: '/android-chrome-192x192.png'
        });

        n.onclick = () => {
          window.focus();
          handleNavigateToSubsection(minute);
          n.close();
        };
      }
    } catch (error) {
      console.warn('Browser notification failed:', error);
    }
  };

  // Navigate to specific subsection
  const handleNavigateToSubsection = (minute: 1 | 5 | 10) => {
    const subsectionMap = { 1: 'min1', 5: 'min5', 10: 'min10' } as const;
    const subsection = subsectionMap[minute];

    // Expand subsection
    setExpandedSections(prev => ({
      ...prev,
      [subsection]: true
    }));

    // Scroll after animation
    setTimeout(() => {
      const element = document.querySelector(
        `.apgar-subsection[data-subsection="${subsection}"]`
      );
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 300);

    // Dismiss notification
    setActiveNotification(null);
  };

  // Trigger notification (beep + browser + in-app)
  const triggerNotification = (minute: 1 | 5 | 10) => {
    // Prevent duplicate sound notifications
    if (!soundPlayedRef.current.has(minute)) {
      soundPlayedRef.current.add(minute);
      playBeep();
    }

    // Show browser notification
    showBrowserNotification(minute);

    // Show in-app notification
    setActiveNotification({ minute });

    // Update notified minutes
    const newNotifiedMinutes = [...(timerState.notifiedMinutes || []), minute];
    const newState = {
      ...timerState,
      notifiedMinutes: newNotifiedMinutes
    };
    setTimerState(newState);
    saveCurrentState({ apgarTimer: newState }, 'apgarTimer');
  };

  // Start timer
  const startTimer = async () => {
    try {
      // Initialize audio context
      initAudio();

      // Request notification permission
      await requestNotificationPermission();

      // Reset sound played tracking
      soundPlayedRef.current.clear();

      const startTime = Date.now();
      const newState: TimerState = {
        startTime,
        isRunning: true,
        notifiedMinutes: []
      };

      // Set current time to start time to avoid flicker
      setCurrentTime(startTime);
      setTimerState(newState);
      saveCurrentState({ apgarTimer: newState }, 'apgarTimer');
    } catch (error) {
      console.error('Failed to start timer:', error);
    }
  };

  // Stop timer
  const stopTimer = (auto: boolean = false) => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    const newState: TimerState = {
      ...timerState,
      isRunning: false
    };

    setTimerState(newState);
    saveCurrentState({ apgarTimer: newState }, 'apgarTimer');
    setActiveNotification(null);
  };

  // Handle timer button click
  const handleTimerButtonClick = () => {
    if (timerState.isRunning) {
      // Check if less than 10 minutes elapsed
      const elapsed = timerState.startTime
        ? Math.floor((Date.now() - timerState.startTime) / 1000)
        : 0;

      if (elapsed < 600) {
        // Show confirmation dialog
        setShowStopDialog(true);
      } else {
        // Auto-stop (already at 10 minutes)
        stopTimer(true);
      }
    } else {
      startTimer();
    }
  };

  // Handle stop confirmation
  const handleStopConfirmed = () => {
    setShowStopDialog(false);
    stopTimer(false);
  };

  // Calculate elapsed seconds (ensure non-negative)
  const elapsedSeconds = timerState.isRunning && timerState.startTime
    ? Math.max(0, Math.floor((currentTime - timerState.startTime) / 1000))
    : 0;

  const calculateColumnScore = (column: 'min1' | 'min5' | 'min10'): number | null => {
    const scores = [
      apgarScores.pulse[column],
      apgarScores.breathing[column],
      apgarScores.muscleTone[column],
      apgarScores.skinColor[column],
      apgarScores.touchResponse[column]
    ];
    
    if (scores.some(score => score === undefined)) {
      return null;
    }
    
    return scores.reduce((sum: number, score) => sum + (score ?? 0), 0);
  };

  const setScore = (criterion: keyof ApgarScores, timeColumn: 'min1' | 'min5' | 'min10', value: number) => {
    setApgarScores(prev => {
      const currentValue = prev[criterion][timeColumn];
      // If clicking the same value again, deselect it
      if (currentValue === value) {
        return {
          ...prev,
          [criterion]: {
            ...prev[criterion],
            [timeColumn]: undefined
          }
        };
      }
      // Otherwise, set the new value
      return {
        ...prev,
        [criterion]: {
          ...prev[criterion],
          [timeColumn]: value
        }
      };
    });
  };

  const clearColumn = (column: 'min1' | 'min5' | 'min10') => {
    setApgarScores(prev => ({
      pulse: { ...prev.pulse, [column]: undefined },
      breathing: { ...prev.breathing, [column]: undefined },
      muscleTone: { ...prev.muscleTone, [column]: undefined },
      skinColor: { ...prev.skinColor, [column]: undefined },
      touchResponse: { ...prev.touchResponse, [column]: undefined }
    }));
  };

  const renderScoreButtons = (criterion: keyof ApgarScores, timeColumn: 'min1' | 'min5' | 'min10') => {
    const currentValue = apgarScores[criterion][timeColumn];
    
    return (
      <div className="score-button-group">
        {[0, 1, 2].map(value => (
          <button
            key={value}
            className={`apgar-score-button ${currentValue === value ? 'selected' : ''}`}
            onClick={() => setScore(criterion, timeColumn, value)}
          >
            {value}
          </button>
        ))}
      </div>
    );
  };

  const toggleSubSection = (section: 'min1' | 'min5' | 'min10') => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const renderSubSection = (column: 'min1' | 'min5' | 'min10', label: string) => {
    const score = calculateColumnScore(column);
    const isExpanded = expandedSections[column];

    return (
      <div className="apgar-subsection" key={column} data-subsection={column}>
        <div
          className={`subsection-header ${isExpanded ? 'expanded' : ''}`}
          onClick={() => toggleSubSection(column)}
        >
          <span className="subsection-title">
            {label}
            {score !== null && ` (ציון ${score})`}
          </span>
          <span className="subsection-toggle">
            {isExpanded ? (
              <ChevronUp size={20} strokeWidth={2.5} />
            ) : (
              <ChevronLeft size={20} strokeWidth={2.5} />
            )}
          </span>
        </div>

        {isExpanded && (
          <div className="subsection-content">
            <div className="criteria-row">
              <span className="criteria-label">דופק</span>
              {renderScoreButtons('pulse', column)}
            </div>
            <div className="criteria-row">
              <span className="criteria-label">נשימה</span>
              {renderScoreButtons('breathing', column)}
            </div>
            <div className="criteria-row">
              <span className="criteria-label">טונוס שרירים</span>
              {renderScoreButtons('muscleTone', column)}
            </div>
            <div className="criteria-row">
              <span className="criteria-label">צבע עור</span>
              {renderScoreButtons('skinColor', column)}
            </div>
            <div className="criteria-row">
              <span className="criteria-label">תגובה למגע</span>
              {renderScoreButtons('touchResponse', column)}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="apgar-section">
      {/* Timer UI */}
      <div className="apgar-timer-container">
        <div className="timer-display">
          <span className="timer-label">שעון:</span>
          <span className="timer-value">{formatTimeMMSS(elapsedSeconds)}</span>
        </div>
        <button
          className={`timer-button ${timerState.isRunning ? 'stop' : 'start'}`}
          onClick={handleTimerButtonClick}
          aria-label={timerState.isRunning ? 'עצור טיימר' : 'התחל טיימר'}
        >
          {timerState.isRunning ? 'עצור' : 'התחל'}
        </button>
      </div>

      <div className="guide-toggle-section">
        <a
          className="guide-toggle-link"
          onClick={(e) => {
            e.preventDefault();
            setShowGuide(!showGuide);
          }}
          href="#"
        >
          {showGuide ? '- ' : '+ '} הצג מדריך שעון אפגר
        </a>

        {showGuide && (
          <div className="apgar-reference-table">
            <table className="apgar-guide-table">
              <thead>
                <tr>
                  <th>מבחן אפגר- מה בודקים?</th>
                  <th>ציון 0</th>
                  <th>ציון 1</th>
                  <th>ציון 2</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>דופק</td>
                  <td>ללא</td>
                  <td>פחות מ100</td>
                  <td>יתר מ-100</td>
                </tr>
                <tr>
                  <td>נשימה</td>
                  <td>ללא</td>
                  <td>איטית</td>
                  <td>סדירה עם בכי</td>
                </tr>
                <tr>
                  <td>טונוס שרירים</td>
                  <td>גפיים רפויות</td>
                  <td>תנועה קלה</td>
                  <td>תנועות מרובות</td>
                </tr>
                <tr>
                  <td>צבע עור</td>
                  <td>חיוור/כחול</td>
                  <td>גפיים כחולות</td>
                  <td>ורוד</td>
                </tr>
                <tr>
                  <td>תגובה למגע</td>
                  <td>אין</td>
                  <td>עיוות פנים</td>
                  <td>בכי</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="apgar-subsections-container">
        {renderSubSection('min1', 'דקה')}
        {renderSubSection('min5', '5 דקות')}
        {renderSubSection('min10', '10 דקות')}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={showStopDialog}
        onClose={() => setShowStopDialog(false)}
        onConfirm={handleStopConfirmed}
        onCancel={() => setShowStopDialog(false)}
        title="עצירת טיימר"
        description="האם אתה בטוח שברצונך לעצור את הטיימר לפני 10 דקות?"
        confirmText="עצור"
        cancelText="ביטול"
      >
        {null}
      </ConfirmationDialog>

      {/* In-App Notification */}
      {activeNotification && (
        <ApgarTimerNotification
          minute={activeNotification.minute}
          onDismiss={() => setActiveNotification(null)}
          onNavigate={() => handleNavigateToSubsection(activeNotification.minute)}
        />
      )}
    </div>
  );
};

export default Apgar;
